import { BridgeClient } from 'app/service/bridge/BridgeClient';

/**
 * Creates a facet values API backed by the bridge transport.
 *
 * Bridge actions used:
 *   facet_values.list        → BridgeUseCase → FacetValueMappingService (discovery + saved merge)
 *   facet_values.update_bulk → BridgeUseCase → FacetValueMappingService.saveMappings()
 *
 * Bridge list response shape:
 *   { result, data: { facet_id, facet_type, values: [{ raw_value, mapped_value, product_count }] } }
 *
 * Admin value model:
 *   { rawValue, mappedValue, productCount }
 *
 * Scope: feature-facet bool/color values only (v1).
 */
export function createBridgeFacetValuesApi() {
  const client = new BridgeClient();

  /**
   * Map a single bridge value record to the admin model.
   */
  function bridgeToAdmin(raw) {
    return {
      rawValue:     raw.raw_value,
      mappedValue:  raw.mapped_value ?? null,
      productCount: raw.product_count != null ? Number(raw.product_count) : 0,
    };
  }

  /**
   * Map an admin value record to the bridge save payload shape.
   */
  function adminToBridge(item) {
    return {
      raw_value:    item.rawValue,
      mapped_value: item.mappedValue ?? null,
    };
  }

  return {
    /**
     * Load values for a specific facet.
     *
     * @param {string} _shopId — not used by bridge (context from plugin side), kept for interface parity
     * @param {string} facetId — facet identifier (e.g. 'washable')
     * @returns {Promise<{ facetId: string, facetType: string, values: Array }>}
     */
    async load(_shopId, facetId) {
      const response = await client.sendMessageWithResponse('facet_values.list', {
        facet_id: facetId,
      });
      const data = response.data || {};
      return {
        facetId:   data.facet_id || facetId,
        facetType: data.facet_type || 'string',
        values:    (data.values || []).map(bridgeToAdmin),
      };
    },

    /**
     * Save value mappings for a specific facet (replace-style).
     *
     * @param {string} _shopId
     * @param {string} facetId
     * @param {Array<{ rawValue: string, mappedValue: string|null }>} mappings
     * @returns {Promise<{ facetId: string, facetType: string, values: Array }>}
     */
    async save(_shopId, facetId, mappings) {
      await client.sendMessageWithResponse('facet_values.update_bulk', {
        facet_id: facetId,
        mappings: mappings.map(adminToBridge),
      });
      // Re-fetch to get the authoritative post-save state
      return this.load(_shopId, facetId);
    },
  };
}
